import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Simple 0-indexed pager for client-side paged tables.
 *
 * Props:
 *  - page:        current 0-based page index
 *  - pageSize:    rows per page
 *  - total:       total number of rows (unpaged)
 *  - onPageChange(nextPage)
 *  - onPageSizeChange(nextSize)   (optional; omit to hide the size selector)
 *  - pageSizeOptions:  array of selectable page sizes
 */
export default function TablePager({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount - 1);
  const from = total === 0 ? 0 : current * pageSize + 1;
  const to = Math.min(total, (current + 1) * pageSize);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gold-subtle" data-testid="table-pager">
      <div className="text-xs text-emerald-muted">
        {total === 0 ? "No results" : `Showing ${from}–${to} of ${total}`}
      </div>
      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-emerald-muted uppercase tracking-widest">Rows</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-8 px-2 rounded-md bg-emerald-elev border border-gold-subtle text-white text-sm focus:outline-none"
              data-testid="table-pager-size"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={current <= 0}
            onClick={() => onPageChange(current - 1)}
            className="h-8 w-8 flex items-center justify-center rounded-md border border-gold-subtle text-emerald-muted hover:text-gold hover:border-gold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="table-pager-prev"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-emerald-muted px-2 tabular-nums">
            Page {current + 1} / {pageCount}
          </span>
          <button
            type="button"
            disabled={current >= pageCount - 1}
            onClick={() => onPageChange(current + 1)}
            className="h-8 w-8 flex items-center justify-center rounded-md border border-gold-subtle text-emerald-muted hover:text-gold hover:border-gold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="table-pager-next"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
